// tutor-frontend/src/pages/Session/Games/lesson-plan/game-list.tsx

import { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { SelectedActivity } from "pages/Session/session-newactivity-types";
import { Subject } from "common/subject";
import { apiClient } from "infra/api-client";
import { BASE_URL } from "global";
import { gamesLookupService } from "modules/games/games-lookup";
import { ActivityGame, GameMetadata, GameProvider } from "modules/games/game"
import { CountryId } from "common/country";
import { ActivityType, LessonActivity } from "modules/lesson/activity/activity.types";
import moment from "moment";
import { accessibleService } from "modules/center-services/AccessibleService";
import { GamePreviewDialog } from "./game-preview-dialog";

type LessonPlan = {
  subjectId: number;
  isHomework: 0 | 1;
};

type AddSelectedActivity = (selectedActivity: SelectedActivity) => void;

type GamesProps = {
  lessonPlan: LessonPlan;
  selectedActivities: SelectedActivity[];
  addSelectedActivity: AddSelectedActivity;
  previouslyAssignedActivities: LessonActivity[];
  country: CountryId;
  lastPlayed: { provider_id: string; provider_game_id: string; last_played: string }[];
  browseMode?: boolean
};

async function fetchGames(): Promise<ActivityGame[]> {
  return gamesLookupService.getGames();
}

export async function fetchAssignedGames(options: {
  session_key: string;
  student_key: string;
}) {
  return apiClient.post(BASE_URL + "games/getAssignedGames", options);
}

export async function fetchLastPlayed(options: {
  student_key: string;
  center_key: string;
}): Promise<GamesProps["lastPlayed"]> {
  return apiClient.post(BASE_URL + "games/getLastPlayed", options);
}

const Game = (props: {
  gameInfo: ActivityGame;
  lessonPlan: LessonPlan;
  addSelectedActivity: AddSelectedActivity;
  disableSelect: boolean;
  locked?: boolean;
  country: CountryId;
  lastPlayed?: string;
  browseMode?: boolean;
}) => {
  const { gameInfo } = props;

  // map subject string to id
  let subjectId = 0;
  switch (gameInfo.subject) {
    case "english":
      subjectId = 1;
      break;
    case "math":
      subjectId = 2;
      break;
  }

  const addGame = () => {
    const selectedActivity: SelectedActivity = {
      activity_type: ActivityType.PLATFORM_GAMES,
      id: gameInfo.id,
      is_homework: props.lessonPlan.isHomework,
      level_id: 0,
      level_name: "",
      name: gameInfo.name,
      subject_id: subjectId,
      topicID: 0,
      topic_name: "Game",
      tutor_comments: "",
      type: "Game",
      coin: GameMetadata.coinsPerGame,
    };

    props.addSelectedActivity(selectedActivity);
  };

  const containerStyle: React.CSSProperties = props.locked
    ? { border: "1px solid #ccc", boxShadow: "5px 5px 5px 0px #E3E8EC", opacity: 0.75 }
    : { border: "1px solid black", boxShadow: "5px 5px 5px 0px #ADC0CE" };


  const [isGamePreviewDialogOpen, setIsGamePreviewDialogOpen] = useState(false);
  const openGamePreviewDialog = () => setIsGamePreviewDialogOpen(true);
  const closeGamePreviewDialog = () => setIsGamePreviewDialogOpen(false);

  const previewGame =() => {
    openGamePreviewDialog()
  }

  function determineGamePreviewSrcUrl(providerGameId: string): string {
    return gamesLookupService.getGamePreviewUrl(providerGameId);
  }

  return (
    <>
      <div>
        <div className="d-flex mb-5 rounded-3 p-2" style={containerStyle}>
          <div style={{ width: "140px", height: "140px", flex: "none" }}>
            <img src={gameInfo.image} style={{ width: "100%", height: "100%" }} />
          </div>
          <div className="mx-3 w-100">
            <div style={{ fontSize: "1.1em", fontWeight: "700" }}>{gameInfo.name}</div>
            <div style={{ fontSize: "1.1em", fontWeight: "700" }}>
              <span>Year Level: </span>
              {gameInfo.getYearLevels(props.country)}
            </div>
            <div style={{ fontSize: "1.1em", fontWeight: "700" }}>
              <span>Topic: </span>
              {gameInfo.description}
            </div>
            <br />

            <div className={"d-flex w-100 justify-content-end align-items-center"}>
              {!props.browseMode && <>
                <div className={"d-flex w-100 justify-content-end align-items-center"}>
                  {props.lastPlayed && (
                    <span className="mr-3" style={{ fontStyle: "italic" }}>
                    Last Played: {moment(props.lastPlayed).format("DD MMM YYYY")}
                  </span>
                  )}

                  {props.locked && (
                    <span className="badge bg-warning text-dark me-2" style={{ border: "1px solid #ffc107" }}>
                      Requires Games add-on
                    </span>
                  )}

                  <Button
                    color={props.locked ? "secondary" : "danger"}
                    onClick={addGame}
                    disabled={props.disableSelect || props.locked}
                    title={props.locked ? "Locked for your centre" : "Add this game"}
                  >
                    {props.locked ? "Locked" : "Select"}
                  </Button>
                </div>
              </>}
              <Button
                color= "danger"
                className="btn btn-outline-danger bg-white text-danger"
                style={{ marginLeft: "3px" }}
                onClick={previewGame}
              >
                Preview
              </Button>
            </div>
          </div>
        </div>
      </div>

      <GamePreviewDialog
        isOpen={isGamePreviewDialogOpen}
        onClose={() => closeGamePreviewDialog()}
        videoSrc={determineGamePreviewSrcUrl(gameInfo.providerGameId)}
        title={gameInfo.name}
      />
    </>
  );
};

export const GameList = (props: GamesProps) => {
  const { lessonPlan, addSelectedActivity, selectedActivities, previouslyAssignedActivities, country } = props;

  const [allGamesInfo, setAllGamesInfo] = useState<ActivityGame[]>([]);
  const [gamesInfo, setGamesInfo] = useState<ActivityGame[]>();
  const [initialised, setInitialised] = useState<boolean>();
  const [accessible, setAccessible] = useState<boolean>();
  const subjectId = lessonPlan.subjectId;

  // robust “mygooroo” constant
  const MYGOOROO = (GameProvider as any)?.MYGOOROO ?? "mygooroo";

  const filterGames = () => {
    let games: ActivityGame[] = [];
    if (subjectId === Subject.MATH) {
      games = allGamesInfo.filter((e) => e.subject === "math");
    } else if (subjectId === Subject.ENGLISH) {
      games = allGamesInfo.filter((e) => e.subject === "english");
    }
    // DO NOT filter by provider—show everything and lock non-MyGooRoo later
    setGamesInfo(games);
  };

  const selectedIds = new Set<number | string>();
  selectedActivities?.forEach((e) => {
    selectedIds.add(e.id!);
  });
  previouslyAssignedActivities?.forEach((e) => {
    selectedIds.add(e.fk_activity_node_id);
  });

  useEffect(() => {
    filterGames();
  }, [subjectId, allGamesInfo]);

  useEffect(() => {
    async function _fetchGames() {
      const _allGames = await fetchGames();
      setAllGamesInfo(_allGames);
    }

    async function _fetchAccessibleServices() {
      await accessibleService.init();
      const _accessible = accessibleService.hasAccessToGames();
      setAccessible(_accessible);
    }

    Promise.all([_fetchGames(), _fetchAccessibleServices()]).then(() => {
      setInitialised(true);
    });
  }, []);

  const banner = (text: string, danger = false) => (
    <div
      className="d-flex justify-content-center mb-2 p-2 font-size-20"
      style={{
        backgroundColor: "#007bff", // match Clear Filter background color
        color: "#ffffff", // match Clear Filter text color
        fontWeight: "bold",
        borderRadius: "4px",
        padding: "0.5rem 1rem",
      }}
    >
      <p className="mb-0" style={{ color: "#ffffff" }}>
        {text}
      </p>
    </div>
  );

  return (
    initialised && (
      <div>
        {accessible
          ? banner("Centre Games add-on is active.")
          : banner(
            "Your centre has not subscribed to the Enrichment & Games bundle. To subscribe, the centre admin can email to admin@mygooroo.io",
            true
          )}

        <div>
          {gamesInfo?.map((gameInfo) => {
            // lock any non-MyGooRoo when centre has no access
            const isLocked =
              !accessible &&
              String(gameInfo.providerId).toLowerCase() !== String(MYGOOROO).toLowerCase();

            const disabled = isLocked || selectedIds.has(gameInfo.id);

            const lastPlayed = props.lastPlayed?.find(
              (e) =>
                e.provider_id === gameInfo.providerId &&
                e.provider_game_id === gameInfo.providerGameId
            )?.last_played;

            return (
              <Game
                key={`${gameInfo.providerId}:${gameInfo.providerGameId}`}
                gameInfo={gameInfo}
                lessonPlan={lessonPlan}
                addSelectedActivity={addSelectedActivity}
                disableSelect={disabled}
                locked={isLocked}
                country={country}
                lastPlayed={lastPlayed}
                browseMode={props.browseMode}
              />
            );
          })}
          {!gamesInfo?.length && (
            <div className="text-center text-muted my-4">No games available for this subject.</div>
          )}
        </div>
      </div>
    )
  );
};
